/**
 * Thin wrappers around TanStack Query so pages don't repeat themselves.
 * Each hook unwraps the wire shape into the shape pages actually want
 * (always an array or { data, meta }).
 */
import {
	useQuery,
	useMutation,
	useQueryClient,
	keepPreviousData,
} from '@tanstack/react-query';

import { apiGet, apiPost, apiDelete, apiPatch } from './client';

// --- In-app notifications (bell + dropdown) --------------------------------
//
// React Query polls every 30s — v0 "real-time" approach. Swap-in for SSE
// later won't change the public hook surface (HANDOVER §8f).
//
// Response/request shapes pulled from the OpenAPI-generated types so the
// FE/BE contract has a single source of truth (production_readiness_guide
// §3.2). No `any` / `AnyRecord`.

import type { paths } from './types.gen';

type AnyRecord = Record<string, any>;

interface Paginated<T> {
	data: T[];
	meta: { page: number; limit: number; total: number; totalPages: number };
}

/** Pull the first array we find in the response, no matter how the
 *  controller wrapped it ({ data }, { items }, { carBrands }, …). */
function unwrapArray<T = AnyRecord>(res: any): T[] {
	if (!res) return [];
	if (Array.isArray(res)) return res;
	if (Array.isArray(res.data)) return res.data;
	for (const v of Object.values(res)) {
		if (Array.isArray(v)) return v as T[];
	}
	return [];
}

// --- Auth -------------------------------------------------------------------
export const meKey = ['me'] as const;
export function useMe() {
	return useQuery({
		queryKey: meKey,
		queryFn: () => apiGet<{ user: AnyRecord }>('/auth/me'),
	});
}

// --- Catalogue --------------------------------------------------------------
export const itemsKey = ['items'] as const;
export function useItems() {
	return useQuery({
		queryKey: itemsKey,
		queryFn: async () => unwrapArray(await apiGet<any>('/items')),
		staleTime: 60 * 60_000,
	});
}

export const carBrandsKey = ['carBrands'] as const;
export function useCarBrands() {
	return useQuery({
		queryKey: carBrandsKey,
		queryFn: async () => unwrapArray(await apiGet<any>('/car_brands')),
		staleTime: 60 * 60_000,
	});
}

// --- Companies (admin / mechanic insurer picker) ----------------------------
export function useCompanies(params: { expertise?: string } = {}) {
	return useQuery({
		queryKey: ['companies', params],
		queryFn: async () =>
			unwrapArray(await apiGet<any>('/companies', { params })),
		staleTime: 60 * 60_000,
	});
}

export interface CompanyMatchingFields {
	id: number;
	name: string;
	expertise: string;
	country_id?: number;
	latitude: number | null;
	longitude: number | null;
	location_label: string | null;
	served_brand_ids: number[] | null;
	is_accepting_requests: boolean;
	coverage_radius_km: number | null;
	far_threshold_km: number | null;
}

export function useCompany(companyId: number | undefined) {
	return useQuery({
		queryKey: ['company', companyId],
		queryFn: () =>
			apiGet<{ company: CompanyMatchingFields }>(`/companies/${companyId}`),
		enabled: !!companyId,
	});
}

interface PreviewMatchesResp {
	matches: { distanceKm: number; flaggedAsFar: boolean }[];
	radiusUsedKm: number | null;
	matchCount: number;
	farCount: number;
}

export function usePreviewMatches(requestId: number | undefined) {
	return useQuery({
		queryKey: ['preview-matches', requestId],
		queryFn: () => apiPost<PreviewMatchesResp>(`/requests/${requestId}/preview-matches`, {}),
		enabled: !!requestId,
		staleTime: 30_000,
	});
}

/**
 * Pre-creation dry-run of the matcher — the wizard form's vehicle coords +
 * brand + optional overrides come in, anonymised matches come out. Powers
 * the inline "Suppliers in range" card in NewRequest (insurance) and
 * NewReport (mechanic-direct). The query is enabled only when we have
 * usable lat/lng; without those the matcher would short-circuit.
 */
export interface PreviewMatchesDryRunInput {
	vehicleLatitude: number | null | undefined;
	vehicleLongitude: number | null | undefined;
	brandId: number | null | undefined;
	radiusOverrideKm: number | null | undefined;
	farThresholdOverrideKm: number | null | undefined;
}

export function usePreviewMatchesDryRun(args: PreviewMatchesDryRunInput) {
	const enabled =
		args.vehicleLatitude != null && args.vehicleLongitude != null;
	return useQuery({
		// Round coords to 4dp so tiny float jitter doesn't cause cache misses.
		queryKey: [
			'preview-matches-dry',
			Number(args.vehicleLatitude || 0).toFixed(4),
			Number(args.vehicleLongitude || 0).toFixed(4),
			args.brandId ?? null,
			args.radiusOverrideKm ?? null,
			args.farThresholdOverrideKm ?? null,
		],
		queryFn: () =>
			apiPost<PreviewMatchesResp>('/requests/preview-matches-dry', {
				vehicleLatitude: args.vehicleLatitude,
				vehicleLongitude: args.vehicleLongitude,
				brandId: args.brandId ?? null,
				radiusOverrideKm: args.radiusOverrideKm ?? null,
				farThresholdOverrideKm: args.farThresholdOverrideKm ?? null,
			}),
		enabled,
		staleTime: 15_000,
		// Don't refetch while the user is typing — the queryKey already
		// dedupes by value, and 15s stale absorbs the typing burst.
		refetchOnWindowFocus: false,
	});
}

export function useUpdateCompany() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			companyId,
			body,
		}: {
			companyId: number;
			body: Partial<{
				name: string;
				latitude: number | null;
				longitude: number | null;
				location_label: string | null;
				served_brand_ids: number[] | null;
				is_accepting_requests: boolean;
				coverage_radius_km: number | null;
				far_threshold_km: number | null;
			}>;
		}) => apiPatch<{ company: CompanyMatchingFields }>(`/companies/${companyId}`, body),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: ['company', vars.companyId] });
			qc.invalidateQueries({ queryKey: ['companies'] });
		},
	});
}

// --- Geography: countries + locations (regions) -----------------------------
export function useCountries() {
	return useQuery({
		queryKey: ['countries'],
		queryFn: async () => unwrapArray(await apiGet<any>('/countries')),
		staleTime: 24 * 60 * 60_000, // changes practically never
	});
}

/**
 * Locations are scoped to the requester's company country by default.
 * Pass { countryId } to fetch a specific country (super-admin / onboarding).
 */
export function useLocations(params: { countryId?: number } = {}) {
	return useQuery({
		queryKey: ['locations', params],
		queryFn: async () =>
			unwrapArray(await apiGet<any>('/locations', { params })),
		staleTime: 60 * 60_000,
	});
}

// --- Insurance: requests ----------------------------------------------------
export const requestsKey = (params?: AnyRecord) =>
	['requests', params || {}] as const;

export function useRequests(
	params: { page?: number; limit?: number; status?: string; q?: string } = {}
) {
	// Strip falsy q so an empty search box doesn't pollute the cache key and
	// doesn't end up as `?q=` on the wire.
	const cleaned: AnyRecord = { ...params };
	if (!cleaned.q || (typeof cleaned.q === 'string' && cleaned.q.trim() === '')) {
		delete cleaned.q;
	}
	return useQuery({
		queryKey: requestsKey(cleaned),
		queryFn: () => apiGet<Paginated<AnyRecord>>(`/requests`, { params: cleaned }),
		placeholderData: keepPreviousData,
	});
}

export function useRequest(requestId: number | undefined) {
	return useQuery({
		queryKey: ['request', requestId],
		queryFn: () => apiGet<{ request: AnyRecord }>(`/requests/${requestId}`),
		enabled: !!requestId,
	});
}

export function useCreateRequest() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: AnyRecord) => apiPost<{ request: AnyRecord }>('/requests', body),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
	});
}

export function useUpdateRequestStatus() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ requestId, status }: { requestId: number; status: string }) =>
			apiPost(`/requests/${requestId}/status`, { status }),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: ['requests'] });
			qc.invalidateQueries({ queryKey: ['request', vars.requestId] });
		},
	});
}

/** Patch mutable fields on an existing request (notes, bid_duration, etc.). */
export function useUpdateRequest() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			requestId,
			body,
		}: {
			requestId: number;
			body: { bid_duration?: string; notes?: string; delivery_time?: string };
		}) => apiPatch(`/requests/${requestId}`, body),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: ['requests'] });
			qc.invalidateQueries({ queryKey: ['request', vars.requestId] });
		},
	});
}

export function useDeleteRequest() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (requestId: number) => apiDelete(`/requests/${requestId}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
	});
}

// --- Quotations -------------------------------------------------------------
export function useQuotationsForRequest(requestId: number | undefined) {
	return useQuery({
		queryKey: ['quotations', 'for-request', requestId],
		queryFn: () => apiGet<{ quotations: AnyRecord[] }>(`/quotations/for-request/${requestId}`),
		enabled: !!requestId,
	});
}

export function useAwardItems() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			requestId,
			awards,
		}: {
			requestId: number;
			awards: { requestQuotationId: number }[];
		}) => apiPost(`/quotations/for-request/${requestId}/award`, { awards }),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: ['request', vars.requestId] });
			qc.invalidateQueries({ queryKey: ['requests'] });
			qc.invalidateQueries({ queryKey: ['quotations', 'for-request', vars.requestId] });
		},
	});
}

// --- Supplier ---------------------------------------------------------------
export function useOpenRequests(params: { page?: number; limit?: number } = {}) {
	return useQuery({
		queryKey: ['supplier', 'open-requests', params],
		queryFn: () => apiGet<Paginated<AnyRecord>>(`/quotations/open-requests`, { params }),
		placeholderData: keepPreviousData,
	});
}

export function useMyQuotations(params: { page?: number; limit?: number } = {}) {
	return useQuery({
		queryKey: ['supplier', 'my-quotations', params],
		queryFn: () => apiGet<Paginated<AnyRecord>>(`/quotations/mine`, { params }),
		placeholderData: keepPreviousData,
	});
}

export function useSubmitQuotation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: AnyRecord) => apiPost('/quotations', body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['supplier'] });
		},
	});
}

// --- Car reports ------------------------------------------------------------
export function useCarReports(params: { page?: number; limit?: number; status?: string } = {}) {
	return useQuery({
		queryKey: ['car-reports', params],
		queryFn: () => apiGet<Paginated<AnyRecord>>('/car-reports', { params }),
		placeholderData: keepPreviousData,
	});
}

export function useCarReport(reportId: number | undefined) {
	return useQuery({
		queryKey: ['car-report', reportId],
		queryFn: () => apiGet<{ report: AnyRecord }>(`/car-reports/${reportId}`),
		enabled: !!reportId,
	});
}

export function useCreateCarReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: AnyRecord) => apiPost<{ report: AnyRecord }>('/car-reports', body),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['car-reports'] }),
	});
}

export function useSubmitCarReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (reportId: number) => apiPost(`/car-reports/${reportId}/submit`),
		onSuccess: (_, reportId) => {
			qc.invalidateQueries({ queryKey: ['car-reports'] });
			qc.invalidateQueries({ queryKey: ['car-report', reportId] });
		},
	});
}

export function useUpdateCarReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ reportId, body }: { reportId: number; body: AnyRecord }) =>
			apiPatch<{ report: AnyRecord }>(`/car-reports/${reportId}`, body),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: ['car-reports'] });
			qc.invalidateQueries({ queryKey: ['car-report', vars.reportId] });
		},
	});
}

export function useDeleteCarReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (reportId: number) => apiDelete(`/car-reports/${reportId}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['car-reports'] }),
	});
}

/**
 * Count of `submitted` car reports waiting on the actor's insurance tenant.
 * Drives the badge next to "Mechanic Reports" in the sidebar. Polled every
 * 30s so it stays roughly fresh without a websocket; explicit invalidation
 * on convert/reject keeps it instant on the actor's own actions.
 */
export const submittedCarReportsKey = ['car-reports', 'submitted-count'] as const;
export function useSubmittedCarReportsCount() {
	return useQuery<{ count: number }>({
		queryKey: submittedCarReportsKey,
		queryFn: () => apiGet<{ count: number }>('/car-reports/submitted-count'),
		refetchInterval: 30_000,
		refetchOnWindowFocus: true,
	});
}

export function useRejectCarReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ reportId, reason }: { reportId: number; reason?: string }) =>
			apiPost(`/car-reports/${reportId}/reject`, { reason }),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: ['car-reports'] });
			qc.invalidateQueries({ queryKey: ['car-report', vars.reportId] });
		},
	});
}

export function useConvertCarReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ reportId, body }: { reportId: number; body?: AnyRecord }) =>
			apiPost(`/car-reports/${reportId}/convert`, body || {}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['car-reports'] });
			qc.invalidateQueries({ queryKey: ['requests'] });
		},
	});
}

type NotificationsListResponse =
	paths['/notifications']['get']['responses'][200]['content']['application/json'];
export type AppNotification = NotificationsListResponse['data'][number];
type MarkReadResponse =
	paths['/notifications/{notificationId}/read']['post']['responses'][200]['content']['application/json'];
type MarkAllReadResponse =
	paths['/notifications/read-all']['post']['responses'][200]['content']['application/json'];

export function useNotifications(params: { unreadOnly?: boolean } = {}) {
	const unreadOnly = params.unreadOnly !== false; // default true
	return useQuery<NotificationsListResponse>({
		queryKey: ['notifications', { unreadOnly }],
		queryFn: () =>
			apiGet<NotificationsListResponse>('/notifications', {
				params: { unreadOnly: unreadOnly ? 1 : 0, limit: 20 },
			}),
		refetchInterval: 30_000,
		refetchOnWindowFocus: true,
		// Keep showing the previous list while the refetch is in flight so the
		// bell count doesn't flicker.
		placeholderData: keepPreviousData,
	});
}

export function useMarkNotificationRead() {
	const qc = useQueryClient();
	return useMutation<MarkReadResponse, unknown, number, { prev: [readonly unknown[], unknown][] }>({
		mutationFn: (notificationId) =>
			apiPost<MarkReadResponse>(`/notifications/${notificationId}/read`),
		// Optimistic update — drop the marked item from the cached unread list
		// so the bell count goes down instantly. The next poll reconciles.
		onMutate: async (notificationId) => {
			await qc.cancelQueries({ queryKey: ['notifications'] });
			const prev = qc.getQueriesData({ queryKey: ['notifications'] });
			qc.setQueriesData<NotificationsListResponse>(
				{ queryKey: ['notifications'] },
				(old) => {
					if (!old) return old;
					const filtered = old.data.filter((n) => n.id !== notificationId);
					return {
						...old,
						data: filtered,
						unreadCount: Math.max(0, old.unreadCount - 1),
					};
				}
			);
			return { prev };
		},
		onError: (_err, _id, ctx) => {
			// Roll back optimistic state if the request failed.
			ctx?.prev?.forEach(([key, value]) => qc.setQueryData(key, value));
		},
		onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
	});
}

export function useMarkAllNotificationsRead() {
	const qc = useQueryClient();
	return useMutation<MarkAllReadResponse, unknown, void>({
		mutationFn: () => apiPost<MarkAllReadResponse>('/notifications/read-all'),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
	});
}

// --- Users / team ----------------------------------------------------------
export function useTeamMembers(params: { page?: number; limit?: number } = {}) {
	return useQuery({
		queryKey: ['team', params],
		queryFn: () => apiGet<Paginated<AnyRecord>>('/users', { params }),
		placeholderData: keepPreviousData,
	});
}

export function useInviteUser() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: { name: string; email: string; password: string; roleId: number }) =>
			apiPost('/users', body),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
	});
}

export function useDeleteUser() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (userId: number) => apiDelete(`/users/${userId}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
	});
}

// --- Attachments (Wave 2 file uploads) --------------------------------------

export interface Attachment {
	id: number;
	parent_type: 'CarReport' | 'Request' | 'RequestItem' | 'Quotation';
	parent_id: number;
	kind: 'accident_photo' | 'part_photo' | 'won_invoice';
	mime: string;
	byte_size: number;
	original_name?: string | null;
	uploaded_by_user_id?: number | null;
	uploaded_by_company_id?: number | null;
	created_at: string;
	/** Server-side path the FE uses for `<img src>` or download links. */
	url: string;
}

export const attachmentsKey = (parentType: string, parentId: number | undefined) =>
	['attachments', parentType, parentId || 0] as const;

/**
 * List attachments attached to a given parent. ACL is enforced server-side;
 * the caller just shows what the API returns.
 */
export function useAttachments(
	parentType: Attachment['parent_type'],
	parentId: number | undefined
) {
	return useQuery({
		queryKey: attachmentsKey(parentType, parentId),
		enabled: !!parentId,
		queryFn: () =>
			apiGet<{ attachments: Attachment[] }>('/attachments', {
				params: { parentType, parentId },
			}).then((r) => r.attachments),
	});
}

/**
 * Upload one or more files. Accepts session JWT through the global axios
 * interceptor; pass `invite` when the caller is on the public WhatsApp flow
 * (the server will accept either credential).
 */
export function useUploadAttachments() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args: {
			parentType: Attachment['parent_type'];
			parentId: number;
			kind: Attachment['kind'];
			files: File[];
			invite?: string;
		}) => {
			const fd = new FormData();
			fd.append('parentType', args.parentType);
			fd.append('parentId', String(args.parentId));
			fd.append('kind', args.kind);
			if (args.invite) fd.append('invite', args.invite);
			for (const f of args.files) fd.append('files', f, f.name);
			// We import apiPost lazily because it doesn't accept the raw
			// FormData header; do a fetch via the underlying client.
			const { api } = await import('./client');
			const res = await api.post<{ attachments: Attachment[] }>('/attachments', fd, {
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			return res.data.attachments;
		},
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: attachmentsKey(vars.parentType, vars.parentId) });
		},
	});
}

/** Soft-delete an attachment (ACL: same as upload). */
export function useDeleteAttachment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (attachmentId: number) =>
			apiDelete(`/attachments/${attachmentId}`),
		// We don't know which parent to refetch, so invalidate every
		// attachments query. There are few of them per page.
		onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments'] }),
	});
}
